import { useEffect, useState } from "react";
import { useVehiclesSocketStore } from "../store/admin/vehiclesSocketStore";
import {
  MAP_READY_TIMEOUT_MS,
  isMapSynced,
  resolveMapReadyStatus,
  type MapReadyOutcome,
  type MapReadySnapshot,
  type MapReadyStatus,
} from "./mapReadyState";

export { MAP_READY_TIMEOUT_MS };
export type { MapReadyStatus };

const readSnapshot = (): MapReadySnapshot => {
  const s = useVehiclesSocketStore.getState();
  return {
    isInitialized: s.isInitialized,
    isConnected: s.isConnected,
    initialSyncDone: s.initialSyncDone,
    awaitingCompanySelection: s.awaitingCompanySelection,
    hasDevices: s.allCompanyDeviceIds.length > 0,
    hasLoadError: s.loadError !== null,
  };
};

export function useMapReady(): { ready: boolean; status: MapReadyStatus } {
  const isInitialized = useVehiclesSocketStore((s) => s.isInitialized);
  const isConnected = useVehiclesSocketStore((s) => s.isConnected);
  const initialSyncDone = useVehiclesSocketStore((s) => s.initialSyncDone);
  const awaitingCompanySelection = useVehiclesSocketStore(
    (s) => s.awaitingCompanySelection,
  );
  const hasDevices = useVehiclesSocketStore(
    (s) => s.allCompanyDeviceIds.length > 0,
  );
  const hasLoadError = useVehiclesSocketStore((s) => s.loadError !== null);

  const snapshot: MapReadySnapshot = {
    isInitialized,
    isConnected,
    initialSyncDone,
    awaitingCompanySelection,
    hasDevices,
    hasLoadError,
  };

  const [outcome, setOutcome] = useState<MapReadyOutcome>(() =>
    isMapSynced(readSnapshot()) ? "synced" : "waiting",
  );

  const synced = isMapSynced(snapshot);

  useEffect(() => {
    if (synced) {
      setOutcome("synced");
      return;
    }
    !isInitialized && setOutcome("waiting");
  }, [synced, isInitialized]);

  useEffect(() => {
    if (outcome !== "waiting") return;
    const id = setTimeout(() => setOutcome("timeout"), MAP_READY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [outcome]);

  return resolveMapReadyStatus(snapshot, outcome);
}
